// Bot protection for Dynamics 365 Marketing forms.
// Injects randomized honeypot fields and blocks submissions that look
// automated, independently for every matching form on the page.
document.addEventListener( 'DOMContentLoaded', function () {
	// Selector for all targeted forms
	const FORM_SELECTOR = "form[class^='marketingForm']";

	// Markers used to identify our own injected elements
	const HONEYPOT_BLOCK_CLASS = 'bo-class-textFormFieldBlock';
	const HONEYPOT_INPUT_CLASS = 'bo-class-input';
	const ERROR_MESSAGE_CLASS = 'bo-class-submit-error';

	// Set to true only for local debugging — renders honeypot fields visibly
	// on the form (red outline) instead of hiding them off-screen, so the
	// injected markup can be inspected manually. Must stay false in production.
	const DEBUG = false;

	// Supported UI languages, English is the fallback
	const SUPPORTED_LANGS = [ 'fi', 'sv', 'en' ];

	const detectPageLang = () => {
		const raw = ( document.documentElement.lang || '' ).toLowerCase();
		const primary = raw.split( '-' )[ 0 ];
		return SUPPORTED_LANGS.indexOf( primary ) !== -1 ? primary : 'en';
	};

	const PAGE_LANG = detectPageLang();

	const ERROR_MESSAGES = {
		fi: 'Lomakkeen täyttämisessä ilmeni ongelma. Päivitä sivu ja yritä lähettää lomake uudelleen.',
		sv: 'Ett problem uppstod när formuläret fylldes i. Uppdatera sidan och försök skicka in formuläret igen.',
		en: 'A problem occurred while filling out the form. Refresh the page and try submitting the form again.',
	};

	// Honeypot field library — one page load uses a random subset of these
	const HONEYPOT_FIELD_LIBRARY = [
		{
			key: 'department',
			name: 'department-bo',
			labels: { fi: 'Osasto', sv: 'Avdelning', en: 'Department' },
		},
		{
			key: 'confirmemail',
			name: 'confirmemail-bo',
			labels: {
				fi: 'Vahvista sähköpostiosoite',
				sv: 'Bekräfta e-postadress',
				en: 'Confirm email address',
			},
		},
		{
			key: 'middlename',
			name: 'middlename-bo',
			labels: { fi: 'Toinen etunimi', sv: 'Mellannamn', en: 'Middle name' },
		},
		{
			key: 'reference',
			name: 'referencenumber-bo',
			labels: {
				fi: 'Viitenumero',
				sv: 'Referensnummer',
				en: 'Reference number',
			},
		},
		{
			key: 'internalcode',
			name: 'internalcode-bo',
			labels: { fi: 'Sisäinen koodi', sv: 'Intern kod', en: 'Internal code' },
		},
		{
			key: 'nickname',
			name: 'nickname-bo',
			labels: { fi: 'Kutsumanimi', sv: 'Smeknamn', en: 'Nickname' },
		},
	];

	// Fisher-Yates shuffle, used so the honeypot selection isn't predictable
	const shuffle = ( list ) => {
		const result = list.slice();
		for ( let i = result.length - 1; i > 0; i-- ) {
			const j = Math.floor( Math.random() * ( i + 1 ) );
			const tmp = result[ i ];
			result[ i ] = result[ j ];
			result[ j ] = tmp;
		}
		return result;
	};

	// Pick 2-3 fields once per page load — every form on the page shares this
	// selection, matching how a single page render would vary the trap fields
	const SELECTED_HONEYPOTS = shuffle( HONEYPOT_FIELD_LIBRARY ).slice(
		0,
		2 + Math.floor( Math.random() * 2 )
	);

	const getFieldLabel = ( fieldDef ) => {
		return fieldDef.labels[ PAGE_LANG ] || fieldDef.labels.en;
	};

	// Map instead of WeakMap because the bootstrap logic needs to iterate
	// over every tracked form to re-check its honeypots after DOM mutations
	const formStates = new Map();
	let formCounter = 0;

	const getSubmitButton = ( form ) => {
		return form.querySelector(
			"button[type='submit'], input[type='submit']"
		);
	};

	// A rendered Dynamics field always carries data-targetproperty; our own
	// honeypot fields never do, so this reliably detects "form is ready"
	// without depending on any specific field name
	const hasRenderedFields = ( form ) => {
		return !! form.querySelector( '[data-targetproperty]' );
	};

	// Hiding is done in _bot-protection.scss (off-screen, !important), not
	// with display:none, because bots that check computed visibility skip
	// display:none fields. aria-hidden keeps assistive tech away from the trap.
	const buildHoneypotField = ( fieldDef, uid ) => {
		const wrapper = document.createElement( 'div' );
		wrapper.className = 'textFormFieldBlock ' + HONEYPOT_BLOCK_CLASS;
		wrapper.setAttribute( 'aria-hidden', 'true' );

		if ( DEBUG ) {
			// Kept visible and clearly marked so the injected markup can be
			// inspected manually — never used in production
			wrapper.classList.add( 'bo-class-debug' );
			wrapper.style.border = '2px dashed #e00';
			wrapper.style.background = '#fff0f0';
			wrapper.style.padding = '4px 8px';
			wrapper.style.margin = '4px 0';
		}

		const label = document.createElement( 'label' );
		label.setAttribute( 'for', uid );
		label.textContent =
			( DEBUG ? '[HONEYPOT] ' : '' ) + getFieldLabel( fieldDef );

		const input = document.createElement( 'input' );
		input.type = 'text';
		input.id = uid;
		input.name = fieldDef.name;
		input.tabIndex = -1;
		// Stops browser/password-manager autofill from filling the trap
		// and blocking a real user
		input.setAttribute( 'autocomplete', 'new-password' );
		input.className = HONEYPOT_INPUT_CLASS;

		wrapper.appendChild( label );
		wrapper.appendChild( input );

		return { wrapper, input };
	};

	const anyHoneypotFilled = ( state ) => {
		return state.honeypotInputs.some(
			( input ) => input.value.trim() !== ''
		);
	};

	// Minimum time between the form being rendered and a submit attempt;
	// anything faster is treated as automated
	const MIN_FILL_MS = 2000;

	// Cheap headless/automation hints. Easy to spoof, but filters lazy bots.
	const looksAutomated = () => {
		const ua = navigator.userAgent || '';
		return (
			navigator.webdriver === true ||
			/HeadlessChrome|PhantomJS/i.test( ua ) ||
			( navigator.languages && navigator.languages.length === 0 )
		);
	};

	// Submissions are judged only when attempted, so a bot never learns
	// which check stopped it.
	const isSuspicious = ( state ) => {
		return (
			anyHoneypotFilled( state ) ||
			! state.interacted ||
			Date.now() - state.renderedAt < MIN_FILL_MS ||
			looksAutomated()
		);
	};

	// Only real user input counts — scripted `el.value = ...` / `el.click()`
	// produce events with isTrusted === false or none at all
	const bindInteractionListeners = ( form, state ) => {
		[ 'keydown', 'pointerdown', 'touchstart' ].forEach( ( type ) => {
			form.addEventListener(
				type,
				( event ) => {
					if ( event.isTrusted ) state.interacted = true;
				},
				true
			);
		} );
	};

	const injectHoneypots = ( form, state ) => {
		// Insert next to a real Dynamics field so the markup blends in with
		// the rest of the form; fall back to appending to the form itself
		const anchor = form.querySelector( '[data-targetproperty]' );
		const anchorBlock = anchor
			? anchor.closest( '.textFormFieldBlock' ) || anchor.parentNode
			: null;

		state.honeypotInputs = [];

		SELECTED_HONEYPOTS.forEach( ( fieldDef ) => {
			const uid = 'bo-' + fieldDef.key + '-' + state.formUid;
			const built = buildHoneypotField( fieldDef, uid );

			if ( anchorBlock && anchorBlock.parentNode ) {
				anchorBlock.parentNode.insertBefore(
					built.wrapper,
					anchorBlock.nextSibling
				);
			} else {
				form.appendChild( built.wrapper );
			}

			state.honeypotInputs.push( built.input );
		} );

		// Start the timing check from the first moment the form is usable
		if ( ! state.renderedAt ) state.renderedAt = Date.now();
	};

	// Called on every relevant DOM mutation: adds honeypots once Dynamics'
	// real fields exist, and re-adds them if a later Dynamics re-render wiped
	// them out
	const ensureHoneypotsPresent = ( form, state ) => {
		if ( state.blocked ) return;
		if ( ! hasRenderedFields( form ) ) return;

		const existing = form.querySelectorAll( '.' + HONEYPOT_INPUT_CLASS );
		if ( existing.length === SELECTED_HONEYPOTS.length ) return;

		// Clear any partial leftovers before rebuilding a clean set
		const stale = form.querySelectorAll( '.' + HONEYPOT_BLOCK_CLASS );
		stale.forEach( ( el ) => {
			el.remove();
		} );

		injectHoneypots( form, state );
	};

	const blockForm = ( form, state ) => {
		if ( state.blocked ) return;
		state.blocked = true;

		const submitButton = getSubmitButton( form );
		if ( submitButton ) {
			submitButton.setAttribute( 'disabled', 'disabled' );
		}

		form.style.display = 'none';

		const errorEl = document.createElement( 'div' );
		errorEl.className = ERROR_MESSAGE_CLASS;
		errorEl.setAttribute( 'role', 'alert' );
		errorEl.style.cssText = 'color:#c0392b;font-weight:600;padding:12px 0;';
		errorEl.textContent = ERROR_MESSAGES[ PAGE_LANG ];

		form.insertAdjacentElement( 'afterend', errorEl );
	};

	const handleSubmit = ( event, form, state ) => {
		if ( state.blocked ) {
			event.preventDefault();
			event.stopImmediatePropagation();
			return;
		}

		if ( isSuspicious( state ) ) {
			event.preventDefault();
			event.stopImmediatePropagation();
			blockForm( form, state );
		}
	};

	const initForm = ( form ) => {
		if ( formStates.has( form ) ) return;

		const state = {
			form,
			formUid: String( formCounter++ ),
			honeypotInputs: [],
			blocked: false,
			interacted: false,
			renderedAt: 0,
		};
		formStates.set( form, state );

		bindInteractionListeners( form, state );

		// Bind as early as possible (before Dynamics' own fields exist) so
		// this listener has the best chance of running before any submit
		// handler Dynamics attaches once it finishes rendering the form
		form.addEventListener( 'submit', ( event ) => {
			handleSubmit( event, form, state );
		} );

		// Dynamics may submit via a button click handler + XHR instead of a
		// native submit event, so also check in the capture phase of the
		// click, before Dynamics' own handler runs
		form.addEventListener(
			'click',
			( event ) => {
				const target = event.target;
				if (
					target &&
					target.closest &&
					target.closest( "button[type='submit'], input[type='submit']" )
				) {
					handleSubmit( event, form, state );
				}
			},
			true
		);

		ensureHoneypotsPresent( form, state );
	};

	const scanForForms = () => {
		document.querySelectorAll( FORM_SELECTOR ).forEach( initForm );
	};

	const refreshTrackedForms = () => {
		formStates.forEach( ( state, form ) => {
			if ( document.body.contains( form ) ) {
				ensureHoneypotsPresent( form, state );
			}
		} );
	};

	const bootstrap = () => {
		scanForForms();
		refreshTrackedForms();

		const observer = new MutationObserver( () => {
			scanForForms();
			refreshTrackedForms();
		} );
		observer.observe( document.body, { childList: true, subtree: true } );
	};

	// Runs on DOMContentLoaded, so document.body already exists here —
	// no extra readiness guard needed
	bootstrap();
} );
